<template>
  <ReportCellWrapper
    :to="params.value.reportLink"
    :trial-over="params.value.trialOver"
  >
    <component
      :is="reportNameToCellInfo[reportType]?.tooltip ? BittsTooltip : 'div'"
      :mount-to-body="true"
      data-testid="report-partner-overlaps-cell"
      overlay-class="report-partner-overlaps-cell__partner-tooltip"
      class="flex items-center gap-4"
    >
      <FontAwesomeIcon
        v-if="reportNameToCellInfo[reportType]?.icon"
        :icon="reportNameToCellInfo[reportType]?.icon || []"
        class="text-neutral-accent h-12 w-12"
      />
      {{ reportNameToCellInfo[reportType]?.text }}
      <template #title>
        <div
          v-if="reportNameToCellInfo[reportType]?.tooltip"
          class="flex items-center flex-wrap gap-8"
        >
          <BittsAvatarItem
            v-for="partner in params.value.partnersInReport"
            :key="partner.id"
            :org="partner"
            :show-initials="true"
            size="small"
            spacing="condensed"
            :title="partner.name"
            class="report-partner-overlaps-cell__partner-avatar"
          />
        </div>
      </template>
    </component>
  </ReportCellWrapper>
</template>

<script setup lang="ts">
import { BittsAvatarItem, BittsTooltip } from '@crossbeam/bitts';

import { ICellRendererParams } from '@ag-grid-community/core';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { computed } from 'vue';

import ReportCellWrapper from '@/components/reports/ReportCellWrapper.vue';

import {
  MULTI_PARTNER_REPORT_TYPES,
  REPORT_TYPE_NAMES,
  type ReportTypeName,
} from '@/constants/reports';
import { ReportCell } from '@/interfaces/reports';
import { usePartnersStore } from '@/stores';

const { params } = defineProps<{ params: ICellRendererParams<ReportCell> }>();

const reportType = computed<ReportTypeName>(() => params.value.reportType);

const partnersStore = usePartnersStore();
function getMultiplePartnerText(): string {
  if (reportType.value === REPORT_TYPE_NAMES.PARTNER_TAGS_TYPE) {
    return (
      partnersStore.getPartnerTagById(params.value.report.tag_id)?.label || '--'
    );
  }
  if (
    !MULTI_PARTNER_REPORT_TYPES.includes(reportType.value) ||
    !params.value.partnersInReport.length
  ) {
    return '--';
  }
  return `${params.value.partnersInReport.length} Partners`;
}
function getSinglePartnerText(): string {
  if (MULTI_PARTNER_REPORT_TYPES.includes(reportType.value)) {
    return '--';
  }
  return params.value.partnersInReport[0]?.name || '--';
}
const reportNameToCellInfo = computed<
  Record<
    ReportTypeName,
    {
      text: string;
      icon?: string[];
      tooltip?: boolean;
    }
  >
>(() => {
  return {
    [REPORT_TYPE_NAMES.ALL_PARTNERS_TYPE]: {
      text: 'All Partners',
      icon: ['fak', 'globe'],
    },
    [REPORT_TYPE_NAMES.CUSTOM_TYPE]: {
      text: getMultiplePartnerText(),
      tooltip: !!params.value.partnersInReport.length,
    },
    [REPORT_TYPE_NAMES.GREENFIELD_TYPE]: { text: getSinglePartnerText() },
    [REPORT_TYPE_NAMES.PARTNER_TAGS_TYPE]: {
      text: getMultiplePartnerText(),
      icon: ['fak', 'tag'],
    },
    [REPORT_TYPE_NAMES.POTENTIAL_REVENUE_TYPE]: {
      text: getSinglePartnerText(),
    },
    [REPORT_TYPE_NAMES.SINGLE_OVERLAPS_TYPE]: { text: getSinglePartnerText() },
  };
});
</script>

<style lang="pcss">
.report-partner-overlaps-cell__partner-avatar {
  .bitts-avatar-item__text-title {
    color: white !important;
  }
}
.report-partner-overlaps-cell__partner-tooltip {
  .ant-tooltip-inner {
    @apply w-[288px];
  }
}
</style>
